import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { OutingShare, OutingSharesResponse, PayShareResponse, Wallet } from '../lib/types'

interface UseSharesOptions {
  /**
   * The shares endpoints 404 (`feature_disabled`) when the server's wallet flag
   * is off — pass `features.group_wallet` so a flagged-off client never asks.
   */
  enabled?: boolean
}

/**
 * What the signed-in member owes on outings somebody else fronted, and paying
 * it from their club wallet.
 *
 * Money truth is server-side. `pay` returns the server's own recomputed share
 * AND wallet, and this hook stores those rather than subtracting locally —
 * a part payment, a cap, or a concurrent settlement all come back as the
 * server's arithmetic, never the client's guess.
 */
export function useShares({ enabled = true }: UseSharesOptions = {}) {
  const [shares, setShares] = useState<OutingShare[]>([])
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [payingId, setPayingId] = useState<string | null>(null)
  const [payError, setPayError] = useState<string | null>(null)
  const [paidWallet, setPaidWallet] = useState<Wallet | null>(null)

  // No synchronous setState in here: the mount effect calls this straight
  // away, and setting state in an effect body triggers a cascading render
  // (react-hooks/set-state-in-effect). `isLoading` is seeded from `enabled`
  // instead, and only ever cleared — exactly how useWallet#refresh is built.
  const refresh = useCallback(() => {
    if (!enabled) return Promise.resolve()
    return api
      .get<OutingSharesResponse>('/golf/shares')
      .then((data) => {
        setShares(data.shares)
        setError(null)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not read what you owe.')
      })
      .finally(() => setIsLoading(false))
  }, [enabled])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /** Settle a share. Omit `amountCents` to pay the whole thing. */
  const pay = useCallback(async (outingId: string, amountCents?: number) => {
    setPayingId(outingId)
    setPayError(null)
    try {
      const data = await api.post<PayShareResponse>(
        `/golf/outings/${encodeURIComponent(outingId)}/contributions`,
        amountCents === undefined ? {} : { amount_cents: amountCents },
      )
      setShares((prev) =>
        prev.map((share) => (share.outing_id === outingId ? data.share : share)),
      )
      setPaidWallet(data.wallet)
      return true
    } catch (e) {
      setPayError(
        e instanceof ApiError
          ? e.message
          : 'That payment did not go through. Nothing was moved — try again.',
      )
      return false
    } finally {
      setPayingId(null)
    }
  }, [])

  return { shares, isLoading, error, pay, payingId, payError, paidWallet, refresh }
}
